import 'leaflet-rotatedmarker'
import { icon, marker, Icon, type Map, type Marker as LeafletMarker } from 'leaflet'
import { useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { routing } from '../../../routes'
import useMapCenterToMarker from '../../../shared/hooks/useMapCenterToMarker'
import useParam from '../../../shared/hooks/useParam'
import { useMapContext } from '../../../shared/contexts/map/MapContext'
import { locationParam, polygonParam, panoHeadingParam } from '../query-params'
import { PAWN_CLASS, MARKER_CLASS } from '../mapStyles'

import useDataSelection from '../../../shared/contexts/DataSelection/useDataSelection'

const defaultIcon = icon({
  iconUrl:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='39' height='39'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath fill='%23666' d='M20.27 1C13.374 1 8 6.27 8 12.946 8 19.927 20.27 38.69 20.27 38.69s12.267-18.897 12.267-25.744C32.537 6.28 27.264 1 20.27 1m0 3.382c5.014 0 8.794 3.682 8.794 8.564 0 .803-.41 4.173-5.703 13.81a172.15 172.15 0 01-3.1 5.394 166.914 166.914 0 01-3.086-5.35c-5.147-9.34-5.702-12.8-5.702-13.854 0-2.33.9-4.493 2.533-6.087 1.637-1.597 3.862-2.477 6.264-2.477'/%3E%3Cpath fill='%23FFF' d='M19.265 35.392C15.373 28.977 8.736 17.045 8.736 12.263c0-5.903 4.627-10.527 10.534-10.527 6.004 0 10.53 4.526 10.53 10.527 0 4.69-6.643 16.678-10.535 23.13'/%3E%3Cpath fill='%23EC0000' d='M19.27 0C12.374 0 7 5.41 7 12.264c0 7.167 12.27 26.43 12.27 26.43s12.267-19.4 12.267-26.43C31.537 5.422 26.264 0 19.27 0m0 3.473c5.014 0 8.794 3.779 8.794 8.79 0 .825-.41 4.285-5.703 14.178a177.988 177.988 0 01-3.1 5.538 172.554 172.554 0 01-3.086-5.491c-5.147-9.59-5.702-13.142-5.702-14.224 0-2.393.9-4.612 2.533-6.249 1.637-1.64 3.862-2.542 6.264-2.542'/%3E%3Cpath fill='red' d='M22.462 11.948a3.266 3.266 0 11-6.533 0 3.266 3.266 0 016.533 0'/%3E%3C/g%3E%3C/svg%3E",
  iconSize: [40, 40],
  iconAnchor: [20, 39],
  className: MARKER_CLASS,
})

const orientationIcon = new Icon({
  iconUrl: '/assets/images/map/panorama-orientation.svg',
  iconSize: [70, 70],
  iconAnchor: [35, 35],
})

const pawnIcon = new Icon({
  iconUrl: '/assets/images/map/panorama-person.svg',
  iconSize: [18, 31],
  iconAnchor: [9, 22],
  className: PAWN_CLASS,
})

const PAWN_OPTS = { icon: pawnIcon, id: 'pawn-marker' }
const ORIENTATION_OPTS = { icon: orientationIcon, id: 'orientation-marker' }
const SEARCH_OPTS = { icon: defaultIcon, id: 'search-marker' }

const useMarkers = (mapInstance: Map | null, showSearchMarker: boolean) => {
  const [position] = useParam(locationParam)
  const { drawToolLocked } = useDataSelection()
  const { panoFullScreen, panoActive } = useMapContext()
  const [polygon] = useParam(polygonParam)
  const location = useLocation()
  const { panToWithPanelOffset } = useMapCenterToMarker(mapInstance)
  const [orientationMarker, setOrientationMarker] = useState<LeafletMarker>()
  const [panoHeading] = useParam(panoHeadingParam)

  useEffect(() => {
    if (orientationMarker && panoHeading) {
      orientationMarker.setRotationAngle(panoHeading)
    }
  }, [orientationMarker, panoHeading])

  useEffect(() => {
    const isMapRoute =
      matchPath({ path: routing.dataDetail.path }, location.pathname) ||
      matchPath({ path: routing.dataSearchGeo.path }, location.pathname)

    if (position && isMapRoute && mapInstance) {
      // This is necessary to call, because we resize the map dynamically
      // We call this again to make sure it's called before re-centre the map to the marker
      // https://leafletjs.com/reference-1.7.1.html#map-invalidatesize
      mapInstance.invalidateSize()
      if (panoFullScreen && panoActive) {
        mapInstance.flyTo(position, 12)
      } else {
        panToWithPanelOffset(position)
        mapInstance.flyTo(position, mapInstance.getZoom())
      }
    }
  }, [position, panoActive, panoFullScreen])

  const markers = useMemo(() => {
    if (drawToolLocked || !position) {
      return []
    }

    if (panoActive) {
      const orientationM = marker(position, ORIENTATION_OPTS)
      setOrientationMarker(orientationM)

      return [orientationM, marker(position, PAWN_OPTS)]
    }

    if (showSearchMarker && !polygon) {
      return [marker(position, SEARCH_OPTS)]
    }

    return []
  }, [drawToolLocked, panoActive, showSearchMarker, position])

  return markers
}

export default useMarkers
